import { ReactNode, useEffect, useState } from "react";
import { Fetcher, FetcherWithComponents, Navigation, useFetchers, useNavigation } from "react-router-dom";
import _ from "lodash";


export default function LoadingIndicator(
  {
    animateWhenReloading = false,
    animateWhenSubmitting = true,
    checkNavigation = false,
    checkAllFetchers = false,
    fetchers,
    isLoading,
    children
  }: {
    animateWhenReloading?: boolean,
    animateWhenSubmitting?: boolean,
    checkNavigation?: boolean,
    checkAllFetchers?: boolean,
    fetchers?: (FetcherWithComponents<any> | undefined)[],
    isLoading?: boolean,
    children?: ReactNode
  }
) {
  const navigation = useNavigation();
  const allFetchers = useFetchers();

  const shouldAnimate = (reactAction: Navigation | Fetcher | FetcherWithComponents<any>) => {
    if (reactAction.state === "idle") {
      return false;
    }

    if (reactAction.state === "submitting") {
      return animateWhenSubmitting;
    }

    if (reactAction.state === "loading") {
      if (reactAction.formData) {
        // reloading after form submission
        return animateWhenReloading;
      } else if (_.has(reactAction, "data") && ((reactAction as Fetcher).data !== undefined)) {
        // refreshing fetcher data
        return animateWhenReloading;
      }
    }

    // in all other cases we should animate
    return true;
  };

  const loading =
    isLoading ||  // check loading flag first as a top priority
    (checkNavigation && shouldAnimate(navigation)) ||
    (checkAllFetchers && allFetchers.some(shouldAnimate)) ||
    (fetchers && _.compact(fetchers).some(shouldAnimate));

  const [ wasLoading, setWasLoading ] = useState(loading);

  useEffect(() => {
    if (loading) {
      setWasLoading(true);
    }
  }, [ loading ]);

  useEffect(() => {
    if (!animateWhenReloading) {
      return;
    }

    if (wasLoading && !loading) {
      // reset wasLoading flag after the fade-in animation is done
      const timeoutHandle = setTimeout(() => {
        setWasLoading(false)
      }, 1500);  // <- the animation is defined in tailwind.config.js
      return () => clearTimeout(timeoutHandle);
    }
  }, [ loading, wasLoading, animateWhenReloading ]);


  return (
    <div className={ loading ? "animate-loading-pulse pointer-events-none" : (wasLoading ? "animate-loading-end-fade-in" : "") }>
      { children }
    </div>
  );
};
